import {Injectable, OnDestroy} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  AppState,
  RouterStateUrl,
} from '../../core.state';

import {Store} from '@ngrx/store';
import {catchError, exhaustMap, filter, map, switchMap, takeUntil, tap, withLatestFrom} from "rxjs/operators";

import {EMPTY, Observable, of, Subject} from "rxjs";

import {TicketClient} from "../../../client/ticket";

import {TicketsScope} from "../tickets.model";
import {
  callSyndicateTicket,
  callSoloTicket,
  callUnAuthTicket,
  createTicket,
  failTicket,
  shareTicket,
  successCreateTicket,
  successShareTicket,
  successTicket,
} from "../tickets.actions";
import { routerNavigationAction} from '@ngrx/router-store';
import {callPool} from '../../pool/pool.actions';
import {SyndicatePortionClient} from "../../../client/syndicate_portions";
import {Router} from '@angular/router';
import {setWebsocketEvent} from '../../coreWebsockets/coreWebsockets.actions';
import {ws} from './../tickets.reducer';
import {selectTicketState} from './../tickets.selector';
import {loadComments} from '../../syndicates/syndicates.actions';

interface WebsocketEvent {
  event: string;
  data?: {
    object_id?: number;
    pool_id?: number;
  };
}

interface TicketState {
  current_ticket?: {
    stringified_id: string;
    pool_id: number;
    syndicate?: {
      stringified_id: string;
    };
  };
}

@Injectable()

export class SoloTicketEffects implements OnDestroy {
  private readonly destroy$ = new Subject<void>();


  callSoloTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(callSoloTicket),
      takeUntil(this.destroy$),
      exhaustMap((propVal: any) => {
        return this.ticketClient.get(propVal.ticket_id).pipe(
          map((data: TicketsScope.TicketItem) => {
            return successTicket({response: data});
          }),
          catchError(error => {
            console.error('[Solo Ticket Effects] Failed to load solo ticket:', error);
            return of(failTicket({error: error}))
          })
        )
      })
    )
  });

  callSyndicateTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(callSyndicateTicket),
      takeUntil(this.destroy$),
      exhaustMap((propVal: any) => {
        return this.syndicatePortionClient.get(propVal.portion_id).pipe(
          map((data: TicketsScope.TicketItem) => {
            return successTicket({response: data});
          }),
          catchError(error => {
            console.error('[Solo Ticket Effects] Failed to load syndicate ticket:', error);
            return of(failTicket({error: error}))
          })
        )
      })
    )
  });

  callUnAuthTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(callUnAuthTicket),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate concurrent requests
      exhaustMap((propVal: any) => {
        return this.ticketClient.unauthTicket(propVal.merchant_ref, propVal.security_code).pipe(
          map((data: TicketsScope.TicketItem) => {
            // Dispatch callPool side effect - pool will be loaded separately
            this.store.dispatch(callPool({ id: data.pool_id }));
            return successTicket({response: data});
          }),
          catchError(error => {
            console.error('[Solo Ticket Effects] Failed to load unauth ticket:', error);
            return of(failTicket({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  failTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(callUnAuthTicket),
      takeUntil(this.destroy$),
      tap((propVal: any) => {
        this._router.navigate(['/', 'errors', 'ticketnotfound']);
      })
    )
  }, {dispatch: false});


  successTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(successTicket),
      takeUntil(this.destroy$),
      exhaustMap((propVal) => {
        if (propVal?.response?.syndicate && propVal.response.syndicate.customer_portion !== '0.0') {

          return of(loadComments({
            params: {
              page: 1,
              model_id: String(propVal.response.syndicate.id),
              feed_type: 'syndicate'
            }
          }))
        }

        return EMPTY;
      })
    )
  }, {dispatch: true});

  createTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(createTicket),
      takeUntil(this.destroy$),
      // Use exhaustMap to prevent duplicate ticket creation
      exhaustMap((model: any) => {
        return this.ticketClient.create(model).pipe(
          map(data => successCreateTicket({model: data})),
          catchError(error => {
            console.error('[Solo Ticket Effects] Failed to create ticket:', error);
            return of(failTicket({error: error}))
          })
        )
      })
    )
  }, {dispatch: true});

  shareTicket = createEffect(() => {
    return this.actions$.pipe(
      ofType(shareTicket),
      takeUntil(this.destroy$),
      switchMap((model: any) => {
        return this.ticketClient.create(model).pipe(
          switchMap(data => {
            return [successShareTicket({model: data})]
          }),
          catchError(error => {
            return of(failTicket({error: error}))
          })
        )
      })
    )
  });

  ////
  routerNavigationTicketItem = createEffect(() => {
    return this.actions$.pipe(
      ofType(routerNavigationAction),
      filter((routerProps) => !(routerProps.payload.routerState as unknown as RouterStateUrl).wallet),
      filter(propVal => propVal.payload.event.url.includes('/tickets')),
      switchMap((routerProps) => {

        const routerState: RouterStateUrl = (routerProps.payload.routerState as unknown as RouterStateUrl);

        // /tickets

        // TicketsSoloView - solo/:ticket_id/pools/:pool_id
        if (routerState.url.includes('/tickets/solo')) {
          const pool_id = routerState.params.pool_id;
          const ticket_id = routerState.params.ticket_id;

          const token: string = routerState.queryParams.token;
          const siteId = routerState.queryParams.siteId;

          if (token && siteId) {
            return [
              callPool({id: pool_id}),
            ];
          } else {
            return [
              callPool({id: pool_id}),
              callSoloTicket({
                ticket_id: ticket_id
              })
            ];
          }
        }

        // TicketsSyndicateItemView - syndicate/:syndicate_id/pools/:pool_id
        // Note: /tickets/group routing is handled by GroupTicketsFeatureModule effects
        if (routerState.url.includes('/tickets/syndicate')) {
          const pool_id = routerState.params.pool_id;
          const syndicate_id = routerState.params.syndicate_id;
          return [
            callPool({id: pool_id}),
            callSyndicateTicket({
              portion_id: syndicate_id
            })
          ]
        }

        // TicketsUnauthItemView - unauth/:ticket_ref/code/:security_code
        if (routerState.url.includes('/unauth') && routerState.url.includes('/code')) {
          const ticket_ref = routerState.params.ticket_ref;
          const security_code = routerState.params.security_code;

          return [
            // ticketMiddleware.loadUnauthTicket(ticket_ref, ticket_code)
            callUnAuthTicket({
              merchant_ref: ticket_ref,
              security_code: security_code,
            })
          ]
        }
        return EMPTY;

      }))
  }, {dispatch: true});

  ///// LIVE UPDATE SOLO TICKET
  setWebsocketEvent = createEffect(() => {
    return this.actions$.pipe(
      ofType(setWebsocketEvent),
      filter((propVal: WebsocketEvent) =>
        ws.RELOAD_EVENTS.concat(Object.keys(ws)).includes(propVal.event)
      ),
      withLatestFrom(
        this.store.select(selectTicketState)
      ),
      switchMap(([event, ticketsState]) => {
        const isSyndicate = !!ticketsState?.current_ticket?.syndicate;

        // Handle ticket refresh events
        if (this.shouldRefreshTicket(event, ticketsState)) {
          return this.getTicketRefreshAction(ticketsState, isSyndicate);
        }

        return EMPTY;
      }),
      catchError((error) => {
        console.error('Websocket effect error:', error);
        return EMPTY;
      })
    );
  }, {dispatch: true})

  constructor(
    public actions$: Actions,
    public store: Store<AppState>,
    public ticketClient: TicketClient,
    public syndicatePortionClient: SyndicatePortionClient,
    private _router: Router
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper functions

  private shouldRefreshTicket(
    event: WebsocketEvent,
    ticketsState: TicketState
  ): boolean {
    if (!ticketsState.current_ticket || !ws.RELOAD_EVENTS.includes(event.event)) {
      return false;
    }

    return !!(
      event.event === 'MICROSITE_AUTHENTICATION_REFRESH' ||
      (event.data?.object_id &&
        Number(ticketsState.current_ticket.pool_id) === Number(event.data.object_id)) ||
      (event.data?.pool_id &&
        Number(ticketsState.current_ticket.pool_id) === Number(event.data.pool_id))
    );
  }

  private getTicketRefreshAction(
    ticketsState: TicketState,
    isSyndicate: boolean
  ): Observable<any> {

    if (!ticketsState.current_ticket) {
      return EMPTY;
    }

    return of(
      isSyndicate
        ? callSyndicateTicket({
          portion_id: Number(ticketsState.current_ticket.syndicate?.stringified_id)
        })
        : callSoloTicket({
          ticket_id: ticketsState.current_ticket.stringified_id
        })
    );
  }

}
